/**
 * Plan Context Utilities
 * 
 * Manages plan selection context across the signup/onboarding flow.
 * Stores plan tier in localStorage with URL parameter support.
 */

import { Platform } from 'react-native';

// Valid plan tiers
export type PlanTier = 'tier1' | 'tier2' | 'tier3';

// Plan tier display names
export const PLAN_TIER_NAMES: Record<PlanTier, string> = {
  tier1: 'Students',
  tier2: 'Chasing Credentials',
  tier3: 'Mentor Coach',
};

// Plan tier features for display
export const PLAN_TIER_FEATURES: Record<PlanTier, string[]> = {
  tier1: [
    '3 AI session analyses/month',
    '120 voice transcription minutes/month',
    '10 chats per day',
    'Basic ICF feedback',
    'Email support',
  ],
  tier2: [
    '5 AI session analyses/month',
    '300 voice transcription minutes/month',
    '25 chats per day',
    'Advanced analytics & insights',
    'ICF exam-ready DOCX export',
    'Progress tracking',
  ],
  tier3: [
    '10 AI session analyses/month',
    '1,200 voice transcription minutes/month',
    '50 chats per day',
    'Advanced analytics & insights',
    'Progress tracking',
    'Custom analysis mode',
  ],
};

const STORAGE_KEY = 'pending_plan_tier';
const CYCLE_STORAGE_KEY = 'pending_plan_cycle';
const CONTEXT_SOURCE_KEY = 'plan_context_source';

export type PlanContextSource = 'landing' | 'app' | 'unknown';

export type PlanCycle = '1mo' | '3mo' | '6mo';

// Billing cycles each tier sells (mirrors the landing pricing cards and the
// plan-selection cycle selector).
export const ALLOWED_PLAN_CYCLES: Record<PlanTier, readonly PlanCycle[]> = {
  tier1: ['1mo'],
  tier2: ['1mo'],
  tier3: ['3mo', '6mo'],
};

export interface PlanContext {
  planTier: PlanTier;
  source: PlanContextSource;
  /** Only set when valid for `planTier`. */
  cycle?: PlanCycle;
}

/**
 * Validates if a string is a valid plan tier
 */
export function isValidPlanTier(tier: string | null | undefined): tier is PlanTier {
  return tier === 'tier1' || tier === 'tier2' || tier === 'tier3';
}

/** Whether `cycle` is one the tier sells (tier1/tier2: 1mo; tier3: 3mo or 6mo). */
export function isValidPlanCycle(tier: PlanTier, cycle: string | null | undefined): cycle is PlanCycle {
  return Boolean(cycle) && (ALLOWED_PLAN_CYCLES[tier] as readonly string[]).includes(cycle as string);
}

/**
 * Plan and cycle from a URL's query (`?plan=tier3&cycle=6mo`). An unknown
 * tier yields null; a cycle the tier doesn't sell is dropped.
 */
export function parsePlanSelectionFromURL(
  href: string | null | undefined,
): { planTier: PlanTier; cycle?: PlanCycle } | null {
  if (!href) return null;
  try {
    const params = new URL(href).searchParams;
    const plan = params.get('plan');
    if (!isValidPlanTier(plan)) return null;
    const cycle = params.get('cycle');
    return isValidPlanCycle(plan, cycle) ? { planTier: plan, cycle } : { planTier: plan };
  } catch {
    return null;
  }
}

/**
 * Gets plan context from localStorage
 */
export function getPlanContext(): PlanContext | null {
  if (Platform.OS !== 'web') {
    // On native, we don't use localStorage - plan is passed via URL params
    return null;
  }

  try {
    const storedTier = localStorage.getItem(STORAGE_KEY);
    const storedSource = localStorage.getItem(CONTEXT_SOURCE_KEY) as PlanContextSource | null;
    const storedCycle = localStorage.getItem(CYCLE_STORAGE_KEY);

    if (isValidPlanTier(storedTier)) {
      return {
        planTier: storedTier,
        source: storedSource || 'unknown',
        ...(isValidPlanCycle(storedTier, storedCycle) ? { cycle: storedCycle } : {}),
      };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Sets plan context in localStorage. Without a `cycle`, a stored cycle is
 * kept when the tier is unchanged (re-setting the same plan from a URL that
 * has no cycle must not forget the landing choice) and dropped otherwise.
 */
export function setPlanContext(
  planTier: PlanTier,
  source: PlanContextSource = 'unknown',
  cycle?: string | null,
): void {
  if (Platform.OS !== 'web') {
    return;
  }

  try {
    const previousTier = localStorage.getItem(STORAGE_KEY);
    localStorage.setItem(STORAGE_KEY, planTier);
    localStorage.setItem(CONTEXT_SOURCE_KEY, source);
    if (isValidPlanCycle(planTier, cycle)) {
      localStorage.setItem(CYCLE_STORAGE_KEY, cycle);
    } else if (cycle != null || previousTier !== planTier) {
      localStorage.removeItem(CYCLE_STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clears plan context from localStorage
 */
export function clearPlanContext(): void {
  if (Platform.OS !== 'web') {
    return;
  }

  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CYCLE_STORAGE_KEY);
    localStorage.removeItem(CONTEXT_SOURCE_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Extracts plan tier from URL query parameters
 * Works on both web (window.location) and native (passed params)
 */
export function getPlanFromURL(): PlanTier | null {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    try {
      const url = new URL(window.location.href);
      const plan = url.searchParams.get('plan');
      if (isValidPlanTier(plan)) {
        return plan;
      }
    } catch {
      // Ignore URL parsing errors
    }
  }
  return null;
}

/**
 * Initializes plan context (tier + billing cycle) from URL parameters.
 * The root layout calls this on app load, before any phase redirect drops
 * the landing page's `?plan=&cycle=` query.
 */
export function initPlanContextFromURL(source: PlanContextSource = 'landing'): PlanContext | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return null;
  }
  const fromUrl = parsePlanSelectionFromURL(window.location.href);

  if (fromUrl) {
    // An explicit `plan` without a valid cycle resets any older cycle.
    setPlanContext(fromUrl.planTier, source, fromUrl.cycle ?? '');
    return { ...fromUrl, source };
  }

  // If no URL param, check localStorage
  return getPlanContext();
}

/**
 * Gets plan context, preferring URL param over localStorage
 */
export function getOrInitPlanContext(urlParamPlan?: string | null, source: PlanContextSource = 'unknown'): PlanContext | null {
  // First check if we have a URL param passed directly
  if (isValidPlanTier(urlParamPlan)) {
    setPlanContext(urlParamPlan, source);
    return getPlanContext() ?? { planTier: urlParamPlan, source };
  }

  // Then check URL
  const fromUrl = Platform.OS === 'web' && typeof window !== 'undefined'
    ? parsePlanSelectionFromURL(window.location.href)
    : null;
  if (fromUrl) {
    setPlanContext(fromUrl.planTier, source, fromUrl.cycle);
    return getPlanContext() ?? { ...fromUrl, source };
  }

  // Finally check localStorage
  return getPlanContext();
}

// Analysis tier access configuration
export interface AnalysisTierAccess {
  allowLevelSelect: boolean;
  allowMarkerTable: boolean;
  allowCustomMode: boolean;
}

export const ANALYSIS_TIER_ACCESS: Record<PlanTier, AnalysisTierAccess> = {
  tier1: { allowLevelSelect: false, allowMarkerTable: false, allowCustomMode: false },
  tier2: { allowLevelSelect: true, allowMarkerTable: true, allowCustomMode: false },
  tier3: { allowLevelSelect: true, allowMarkerTable: true, allowCustomMode: true },
};

export function getAnalysisTierAccess(tier?: PlanTier | string | null): AnalysisTierAccess {
  if (tier && ANALYSIS_TIER_ACCESS[tier as PlanTier]) {
    return ANALYSIS_TIER_ACCESS[tier as PlanTier];
  }
  return ANALYSIS_TIER_ACCESS.tier1; // default to most restrictive
}

/**
 * Builds a URL with plan query parameter
 */
export function appendPlanToURL(baseUrl: string, planTier: PlanTier): string {
  try {
    const url = new URL(baseUrl);
    url.searchParams.set('plan', planTier);
    return url.toString();
  } catch {
    // If URL parsing fails, append manually
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}plan=${planTier}`;
  }
}

/**
 * Gets the display name for a plan tier
 */
export function getPlanDisplayName(tier: PlanTier): string {
  return PLAN_TIER_NAMES[tier] || tier;
}

/**
 * Gets features for a plan tier
 */
export function getPlanFeatures(tier: PlanTier): string[] {
  return PLAN_TIER_FEATURES[tier] || [];
}
